import { parse as parsePath } from 'path'
import { invariant } from '../lib/invariant'

export type ProjectStore = 'userFileSystem' | 'originPrivateFileSystem'

export type ProjectPath = `/${string}`

export type ProjectFile = {
  store: ProjectStore
  name: string
  path: ProjectPath
  fileHandle: FileSystemFileHandle | null
}

export type ProjectFolder = {
  store: ProjectStore
  name: string
  path: ProjectPath
  items: ProjectItem[]
  directoryHandle: FileSystemDirectoryHandle | null
}

export type ProjectItem = ProjectFile | ProjectFolder

export function isProjectFolder(item: ProjectItem): item is ProjectFolder {
  return 'items' in item
}

export async function areProjectItemsEqual(aList: ProjectItem[], bList: ProjectItem[]) {
  if (aList.length !== bList.length) {
    return false
  }
  for (let i = 0; i < aList.length; i++) {
    const a = aList[i]
    const b = bList[i]
    if (a.store !== b.store) {
      return false
    }
    if (a.name !== b.name) {
      return false
    }
    if (a.path !== b.path) {
      return false
    }
    if (isProjectFolder(a) && isProjectFolder(b)) {
      if ((a.directoryHandle === null) !== (b.directoryHandle === null)) {
        return false
      }
      if (a.directoryHandle && b.directoryHandle && !(await a.directoryHandle.isSameEntry(b.directoryHandle))) {
        return false
      }
      if (!(await areProjectItemsEqual(a.items, b.items))) {
        return false
      }
    } else if (!isProjectFolder(a) && !isProjectFolder(b)) {
      if ((a.fileHandle === null) !== (b.fileHandle === null)) {
        return false
      }
      if (a.fileHandle && b.fileHandle && !(await a.fileHandle.isSameEntry(b.fileHandle))) {
        return false
      }
    } else {
      return false
    }
  }
  return true
}

export function visitProjectItems(
  topLevelItems: ProjectItem[],
  visitor: (item: ProjectItem, parentFolder: ProjectFolder | null) => boolean | undefined
) {
  const visitItem = (item: ProjectItem, parentFolder: ProjectFolder | null): boolean | undefined => {
    let stop = visitor(item, parentFolder)
    if (stop) {
      return true
    }
    if (isProjectFolder(item)) {
      const children = [...item.items] // guard against mutation
      for (const child of children) {
        stop = visitItem(child, item)
        if (stop) {
          return true
        }
      }
    }
  }
  const topLevelItemsCopy = [...topLevelItems] // guard against mutation
  for (const topLevelItem of topLevelItemsCopy) {
    const stop = visitItem(topLevelItem, null)
    if (stop) {
      return
    }
  }
}

export function findProjectItemWithPath(topLevelItems: ProjectItem[], path: string) {
  let foundItem: ProjectItem | undefined
  visitProjectItems(topLevelItems, (item) => {
    if (item.path === path) {
      foundItem = item
      return true
    }
  })
  return foundItem
}

export function getProjectItemParentFolder(topLevelItems: ProjectItem[], item: ProjectItem): ProjectFolder | null {
  invariant(item.path.startsWith('/'), `Invalid project path '${item.path}'`)
  if (!item.path.slice(1).includes('/')) {
    return null
  }
  const parentPath = item.path.slice(0, item.path.lastIndexOf('/'))
  const parentItem = findProjectItemWithPath(topLevelItems, parentPath)
  invariant(
    parentItem && isProjectFolder(parentItem),
    `Could not find parent folder for project item at path '${item.path}'`
  )
  return parentItem
}

const rebaseItem = (item: ProjectItem, oldBasePath: ProjectPath, newBasePath: ProjectPath) => {
  if (item.path.startsWith(`${oldBasePath}/`)) {
    item.path = `${newBasePath}${item.path.slice(oldBasePath.length)}`
    if (isProjectFolder(item)) {
      item.items.forEach((child) => rebaseItem(child, oldBasePath, newBasePath))
    }
  }
}

export function renameProjectItem(topLevelItems: ProjectItem[], item: ProjectItem, newName: string): ProjectItem[] {
  const parentFolder = getProjectItemParentFolder(topLevelItems, item)

  const oldPath = item.path
  const newPath: ProjectPath = `${parentFolder?.path ?? ''}/${newName}`

  item.name = newName
  item.path = newPath
  if (isProjectFolder(item)) {
    item.items.forEach((child) => rebaseItem(child, oldPath, newPath))
  }

  return [...topLevelItems] // ensure Object.is on snapshot returns false
}

export function moveProjectItem(
  topLevelItems: ProjectItem[],
  item: ProjectItem,
  newParentFolder: ProjectFolder | null,
  newName?: string
): ProjectItem[] {
  const oldParentFolder = getProjectItemParentFolder(topLevelItems, item)
  if (oldParentFolder === newParentFolder) {
    return topLevelItems // same instance, no-op
  }

  let newTopLevelItems = [...topLevelItems]

  const oldPath = item.path
  const newPath: ProjectPath = `${newParentFolder?.path ?? ''}/${newName ?? item.name}`

  item.name = newName ?? item.name
  item.path = newPath
  if (isProjectFolder(item)) {
    item.items.forEach((child) => rebaseItem(child, oldPath, newPath))
  }

  if (oldParentFolder) {
    oldParentFolder.items = oldParentFolder.items.filter((child) => child !== item)
  } else {
    newTopLevelItems = topLevelItems.filter((child) => child !== item)
  }

  if (newParentFolder) {
    newParentFolder.items = [...newParentFolder.items, item]
    newParentFolder.items.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
    newTopLevelItems = [...newTopLevelItems] // ensure Object.is on snapshot returns false
  } else {
    newTopLevelItems = [...newTopLevelItems, item]
    newTopLevelItems.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
  }

  return newTopLevelItems
}

export function removeProjectItem(topLevelItems: ProjectItem[], item: ProjectItem) {
  let newTopLevelItems = [...topLevelItems]

  const parentFolder = getProjectItemParentFolder(newTopLevelItems, item)
  if (parentFolder) {
    parentFolder.items = parentFolder.items.filter((child) => child !== item)
  } else {
    newTopLevelItems = newTopLevelItems.filter((child) => child !== item)
  }

  return newTopLevelItems
}

export function nameForProposedName(topLevelItems: ProjectItem[], proposedName: string, folder: ProjectFolder | null) {
  const incrementName = (originalName: string) => {
    const { name, ext } = parsePath(originalName)
    let dashIndex = name.lastIndexOf('-')
    if (dashIndex >= 0) {
      const suffix = name.slice(dashIndex + 1)
      if (suffix.match(/^\d*$/)) {
        const count = parseInt(suffix)
        return `${name.slice(0, dashIndex)}-${count + 1}${ext}`
      }
    }
    return `${name}-1${ext}`
  }

  const existingNames = (folder ? folder.items : topLevelItems).map((item) => item.name)
  let candidateName = proposedName
  while (existingNames.includes(candidateName)) {
    candidateName = incrementName(candidateName)
  }
  return candidateName
}
